import {Hub} from 'aws-amplify/utils';
import {
    fetchUserAttributes,
    fetchAuthSession,
    signIn,
    confirmSignIn,
    signOut,
    confirmResetPassword, SignInOutput,
} from 'aws-amplify/auth';
import {
    CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {UserPasswordStatus} from '../model/update-password-status';
import {ChangePasswordState} from '../model/change-password-state';
import {AwsTokenState} from '../model/aws-token-state';
import {
    configSettings,
    getConfiguration,
} from '../../shared/configuration/config-settings';
import {cookieDomain} from './cookie-domain';
import {setLogoutCookie} from '../components/session-timeout';


export interface UserInfo {
    isAuthenticated: boolean;
    email: string | undefined;
}

export interface LoginValues {
    email: string,
    password: string
}

export const GENERIC_AUTHENICATION_ERROR: string = "There is an issue with your Login. Please contact your system administrator.";
export const VERIFICATIONCODE_AUTHENICATION_ERROR: string = "Verification failed. Log in again to proceed.";
export const PASSWORD_UPDATED_SUCCESS: string = "Password Updated. Log in with your new password.";
export const PASSWORD_UPDATED_ERROR: string = "There is an issue updating your password please contact system administrator.";
export const PASSWORD_VERIFICATION_ERROR: string = "Verification Failed.  Log in again to proceed.";
export const INVALID_DOMAIN_ERROR : string = "Please use a valid email.";
export const INVALID_COGNITO_STATE: string = 'User password cannot be reset in the current state.'
export const PASSWORD_RESET_ATTEMPT_LIMIT_EXCEEDED: string = 'Attempt limit exceeded, please try after some time.'

/* Amplify Auth State and UI Slots Mapping
    signin: "sign-in",
    confirmSignIn: "confirm-sign-in",
    signup: "sign-up",
    confirmSignUp: "confirm-sign-up",
    forgotpassword: "forgot-password"
    TOTPSetup: "totp-setup"
    confirmSignIn: "confirm-sign-in"
    confirmSignUp: "confirm-sign-up"
    forgotpassword: "forgot-password"
    loading: "loading"
    resettingpassword: "require-new-password"
    signin: "sign-in"
    signup: "sign-up"
    verifyContact: "verify-contact"
*/
export function changeAuthState(authState: string) {
  Hub.dispatch('UI Auth', {
    event: 'AuthStateChange',
    message: authState
  });
};

export enum AuthView {
    SignIn = 'signin',
    ConfirmSignIn = 'confirmSignIn',
    ConfirmSignUp = 'confirmSignUp',
    ForgotPassword = 'forgotpassword',
    ResettingPassword = 'resettingpassword'
}

class AuthorizationService {

    async getLoggedInUser(): Promise<UserInfo | undefined> {
        const userAttributes = await fetchUserAttributes()
        try{
            return {
                isAuthenticated: !!userAttributes,
                email: userAttributes?.email
            };
        }catch(e){
            console.error(e)
            return {
                isAuthenticated: false,
                email: ""
            }
        }
    }

    private currentUser: any;
    private currentUserSession: CognitoUserSession | undefined;

    async checkForUsersession() : Promise<UserInfo | undefined> {
        return await this.getLoggedInUser();
    }

    async fetchSecurityToken() {
        let securityToken = {} as AwsTokenState;

        await fetchAuthSession().then(response => {

            let accessToken = response?.tokens?.accessToken?.toString()!
            let jwtIdToken = response?.tokens?.idToken?.toString()!

            securityToken = {
                jwtAccessToken: accessToken,
                jwtIdToken: jwtIdToken,
            };
        });
        return securityToken;
    }
    checkforChallengeAndRedirect(authenticatedUser: SignInOutput) {
        const nextStep = authenticatedUser?.nextStep;
        if (!nextStep) return true;
        const signInStep = nextStep.signInStep;
        switch (signInStep) {
            case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                changeAuthState(AuthView.ConfirmSignIn);
                return false;
            case 'CONFIRM_SIGN_UP':
                changeAuthState(AuthView.ConfirmSignUp);
                return false;
            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                changeAuthState(AuthView.ResettingPassword);
                return false;
            case 'RESET_PASSWORD':
                changeAuthState(AuthView.ForgotPassword);
                return false;
            case 'DONE':
                return true;
            default:
                return true;
        }
    }

    async login(username: string, password: string ): Promise<boolean> {
        try {
            const authenticatedUser: SignInOutput = await signIn({ username, password });
            this.currentUser = authenticatedUser;
            const isUserAuthenticated = this.checkforChallengeAndRedirect(authenticatedUser);

            if (isUserAuthenticated) {
                const session = await fetchAuthSession();
                const accessToken = session?.tokens?.accessToken?.toString();
                const idToken = session?.tokens?.idToken?.toString();
                if (!accessToken || !idToken) {
                    throw new Error(GENERIC_AUTHENICATION_ERROR);
                }
                this.currentUserSession = undefined;
            }

            return isUserAuthenticated;

        } catch (error: any) {

            const isTempPasswordExpired =
                error?.name === 'NotAuthorizedException' &&
                typeof error?.message === 'string' &&
                /temporary password has expired/i.test(error.message.toLowerCase());

            const isUserAlreadySignedIn =
                error?.name === 'UserAlreadyAuthenticatedException';

            const errorMessage = isTempPasswordExpired ?
                "Temporary password has expired. Click Forgot Password link to receive a new temporary password." :
                isUserAlreadySignedIn ?
                `${error.message} Please refresh the screen. If the problem persists please contact FinPay support.` :
                error.message

            throw new Error (errorMessage);

        }
    }

    async verifySigninCode(code: string): Promise<boolean> {
        try {
            await confirmSignIn({challengeResponse: code});
            return true;
        } catch (error) {
            // TODO: Log error for Audit
            return false; // Thunk will handle navigation back to login screen
        }
    }

    async logout(): Promise<boolean> {
        try {
            await signOut({
                //Revokes refresh token
                global: true
            })
            return true;
        } catch (error) {
            // TODO: Log error for Audit
            throw new Error (GENERIC_AUTHENICATION_ERROR);
        }finally{
            setLogoutCookie();
            this.clearCognitoCookies();
        }
    }

    clearCognitoCookies() {
        document.cookie.split(";").forEach(cookie => {
            const cookieName = cookie.split("=")[0].trim();

            if (cookieName.includes("CognitoIdentityServiceProvider")) {
                document.cookie = `${cookieName}=; path=/; domain=${cookieDomain || window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
            }
        });
    }

    async sendAccHolderNotification(params: {username: string, userRoleId?: number}): Promise<UserPasswordStatus>{

        const {username, userRoleId} = params;

        const config = getConfiguration();

        return new Promise((resolve) => {

            const passwordStatus = new UserPasswordStatus();

            const userPool = new CognitoUserPool({
                UserPoolId: userRoleId === 4 ? configSettings.aws_patientPoolId : config.aws_userPoolId,
                ClientId: userRoleId === 4 ? configSettings.aws_patientPoolWebClientId: config.aws_userPoolWebClientId
            })

            const cognitoUser = new CognitoUser({
                Username: username,
                Pool: userPool
            })

            cognitoUser.forgotPassword({
                onSuccess: () => {
                    passwordStatus.passwordUpdated = true;
                    passwordStatus.statusMessage = "";
                    resolve(passwordStatus);
                },
                onFailure: (error: any) => {

                    passwordStatus.passwordUpdated = false;
                    switch(true){
                        case error.code === 'NotAuthorizedException' && error.message.includes("reset in the current state"):
                            passwordStatus.statusMessage = INVALID_COGNITO_STATE
                            break;
                        case error.code === 'LimitExceededException':
                            passwordStatus.statusMessage = PASSWORD_RESET_ATTEMPT_LIMIT_EXCEEDED
                            break;
                        default:
                            passwordStatus.statusMessage = GENERIC_AUTHENICATION_ERROR;
                    }

                    resolve(passwordStatus)

                }
            })
        })
    }

    async SetPassword(newPassword: string) : Promise<UserPasswordStatus> {
        const passwordStatus = new UserPasswordStatus();
        try {
            await confirmSignIn({challengeResponse: newPassword});

            passwordStatus.passwordUpdated = true;
            passwordStatus.statusMessage = PASSWORD_UPDATED_SUCCESS;

            return passwordStatus;
        } catch (error) {
            // TODO: Log error for Audit
            passwordStatus.passwordUpdated = false;
            passwordStatus.statusMessage = PASSWORD_UPDATED_ERROR;

            return passwordStatus;
        }
    }

    async SetPasswordWithVerificationCode(username: string, changePassword: ChangePasswordState) : Promise<UserPasswordStatus> {
        const passwordStatus = new UserPasswordStatus();
        try {
            await confirmResetPassword({
                username,
                confirmationCode: changePassword.verificationCode,
                newPassword: changePassword.newPassword
            });

            passwordStatus.passwordUpdated = true;
            passwordStatus.statusMessage = PASSWORD_UPDATED_SUCCESS;

            return passwordStatus;
        } catch (error) {
            // TODO: Log error for Audit
            passwordStatus.passwordUpdated = false;
            passwordStatus.statusMessage = PASSWORD_VERIFICATION_ERROR;

            return passwordStatus;
        }
    }
}



export const authService = new AuthorizationService();
